import React, {useState, useEffect, useRef} from 'react';
import { 
    Flex, Text, Button, HStack, VStack, Checkbox, Image, Divider, Link, Select,
    TableContainer, Table, Thead, Tbody, Tfoot, Tr, Th, Td, 
    InputGroup, Input, InputLeftAddon, Badge, Icon,
    Center, CircularProgress, CircularProgressLabel, useToast, useDisclosure
} from '@chakra-ui/react';
import { FormattedDate, FormattedTime } from "react-intl";
import { useCookies } from "react-cookie";
import axios from 'axios';
import { FaWallet, FaSearch } from 'react-icons/fa';
import { MdCancel, MdSkipNext, MdSkipPrevious } from 'react-icons/md';
import { sleep } from "../../utils";
import httpClient from '../../services/http-client';
import { PAGINATION_LIST, SOLANA_TOKEN_ADDRESS_MAX_LENGTH, SOLANA_TOKEN_ADDRESS_MIN_LENGTH } from '../../constants2';
import { confirmChoiceAlert } from "../../utils/confirmChoice";

const TABLE_HEADERS = [
    { id: 1, title: "Number" },
    { id: 2, title: "Ticker" },
    // { id: 3, title: "Ticker Address" },
    { id: 3, title: "Long/Short" },
    { id: 4, title: "Price When Buy" },
    { id: 5, title: "Price When Sold" },
    { id: 6, title: "Profit/Stop Lost (%)" },
    { id: 7, title: "Buy At" },
    { id: 8, title: "Sold At" },
    { id: 9, title: "Is Finished" },
    { id: 10, title: "Created At" },
]

function ListAutoTradeTransaction({ }) {
    const [ cookies, setCookie, removeCookie ] = useCookies(["loginCookie"]);

    const toast = useToast();
    const mouted = useRef();
    const initialRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isFirstUpdateTransaction, setIsFirstUpdateTransaction] = useState(true);
    const [ isShowNotFinished, setIsShowNotFinished ] = useState(false);
    const [allTransactions, setAllTransactions] = useState([]);
    const [autoTradeConfig, setAutoTradeConfig] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if(!mouted.current) {
            getTransactions();
            getAutoTradeConfig();
            mouted.current = true;
        }
    }, []);

    useEffect(() => {
        getTransactions();
    }, [limit, currentPage, isShowNotFinished]);

    useEffect(() => {
        if((allTransactions.length > 0) && autoTradeConfig) {
            if(isFirstUpdateTransaction) {
                updateTransactionsIsNotFinished();
                setIsFirstUpdateTransaction(false);
            }

            const intervalId = setInterval(async() => {
                updateTransactionsIsNotFinished();
            }, 10000);
            return () => clearInterval(intervalId);
        }

    }, [allTransactions, autoTradeConfig]);

    const getTransactions = async() => {
        setIsLoading(true);
        await sleep(250);

        let query = `offset=${(currentPage - 1)*limit}&&limit=${limit}&&searchText=${searchText}&&isNotFinished=${isShowNotFinished}`;  
        const res = await httpClient.get(`/tradingview/auto_trade/transactions?${query}`,   
            {
                auth: {
                    username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                    password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                }
            }
        );

        if(res.data?.error) {
            toast({
                description: "Error when get data!!!",
                status: "error",
                duration: 2000,
                isClosable: true,
                position: "bottom-right"
            });
        } else { 
            setAllTransactions(res.data?.data?.records);
            setTotalCount(res.data?.data?.totalCount);
        }

        setIsLoading(false);
    }

    const getAutoTradeConfig = async() => {
        const res = await httpClient.get(`/tradingview/auto_trade/configs`,   
            {
                auth: {
                    username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                    password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                }
            }
        );

        if(res.data?.error) {
            toast({
                description: "Error when get data!!!",
                status: "error",
                duration: 2000,
                isClosable: true,
                position: "bottom-right"
            });
        } else { 
            setAutoTradeConfig(res.data?.data);
        }
    }

    const updateTransactionsIsNotFinished = async() => {
        const FINANCIAL_LEVERAGE = autoTradeConfig.financial_leverage;
        let findTransactionsIsFinished = allTransactions.filter(item => item.is_finished === true);
        let findTransactionsIsNotFinished = allTransactions.filter(item => item.is_finished === false);
        await Promise.all(findTransactionsIsNotFinished.map(async(item) => {
            const { ticker_address, isLongOrder, price_when_buy } = item;
            let currentTokenPriceUSD = await getPriceToken(ticker_address);
            let price_change_percentage = isLongOrder 
                ? (((currentTokenPriceUSD - price_when_buy)*FINANCIAL_LEVERAGE)/price_when_buy)*100
                : (((price_when_buy - currentTokenPriceUSD)*FINANCIAL_LEVERAGE)/price_when_buy)*100;
            price_change_percentage = Math.round(price_change_percentage *100)/100;

            item.currentTokenPriceUSD = currentTokenPriceUSD;
            item.currentPriceChangePercentage = price_change_percentage;
        }))
        setAllTransactions([...findTransactionsIsNotFinished, ...findTransactionsIsFinished]);
    }

    const getPriceToken = async(token_address) => {
        const response = await axios.get(`https://api.dexscreener.com/latest/dex/tokens/${token_address}`)
        let tokenPriceUSD = null;
        const pairsData = response.data?.pairs;

        if(pairsData?.length > 0) {
            for(let i = 0; i < pairsData.length; i++) {
                if((pairsData[i]["quoteToken"]["symbol"] === "USDC") || (pairsData[i]["quoteToken"]["symbol"] === "USDT")) {
                    tokenPriceUSD = pairsData[i]["priceUsd"];
                    break;
                }
            }
        }

        return tokenPriceUSD;
    }

    const handleNextPage = async() => {
        const newCurrentPage = currentPage + 1;
        setCurrentPage(newCurrentPage);
    }

    const handlePreviousPage = () => {
        setCurrentPage(currentPage - 1);
    }

    const handleSearch = () => {
        getTransactions();
    }

    const handleCancelOrder = (id) => {
        confirmChoiceAlert(async() => {
            const res = await httpClient.put(`/tradingview/auto_trade/transactions/${id}`,  
                {
                    is_finished: true
                },
                {
                    auth: {
                        username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                        password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                    }
                }
            );

            if(res.data?.error) {
                toast({
                    description: "Error when get data!!!",
                    status: "error",
                    duration: 2000,
                    isClosable: true,
                    position: "bottom-right"
                });
            } else { 
                toast({
                    description: "Cancel order successful!",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                    position: "bottom-right"
                });
                await sleep(1000);
                getTransactions();
            }
        })
    }

    return (
        <Flex flexDirection="column" align="flex-start" w="fit-content" h="fit-content">
            <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} p={{ base: 2, md: 4, lg: 4 }}
                opacity={isLoading ? 0.3 : 1} pointerEvents={isLoading ? "none" : "visible"}
            >
                <Flex flexDirection="column" mx={{ base: 1, md: 3, lg: 3 }} my={{ base: 2, md: 0, lg: 0 }} mt={8} gap={2}>
                    <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} justify="space-between" align="center" p={{ base: 0, md: 2, lg: 2 }} gap={2}>
                        <Checkbox size="sm" color="wheat" onChange={(evt) => setIsShowNotFinished(evt.currentTarget.checked)}>Only order active</Checkbox>
                        <InputGroup maxW="600px" color="gray.300" borderColor="gray.500">
                            <InputLeftAddon backgroundColor="transparent">
                                <FaSearch color="gray.300"/>
                            </InputLeftAddon>
                            <Input type='text' placeholder="Search ..." borderLeftWidth={0} value={searchText}
                                onChange={(evt) => setSearchText(evt.currentTarget.value)} 
                                onKeyUp={(evt) => {
                                    if(evt.key === "Enter") {
                                        handleSearch();
                                    }
                                }}
                            />
                        </InputGroup>
                    </Flex>

                    <TableContainer borderColor="gray.500" borderWidth="1px" borderRadius={4}>
                        <Table variant="simple" size="sm" overflowX="scroll">
                            <Thead>
                                <Tr height={10} backgroundColor="#673ab7">
                                    {
                                        TABLE_HEADERS.map(th => (
                                            <Th key={th.id} textTransform="none" borderBottom={0}>
                                                <Text color="white" fontSize="12px" fontWeight="normal">{th.title}</Text>
                                            </Th>
                                        ))
                                    }
                                </Tr>
                            </Thead>
                            <Tbody>
                                {
                                    allTransactions.length > 0 && allTransactions.map((transaction, idx) => {
                                        const { 
                                            id, ticker, ticker_address, isLongOrder, price_when_buy, buy_at, sold_at, is_finished, created_at 
                                        } = transaction;

                                        let price_when_sold = transaction.price_when_sold || transaction.currentTokenPriceUSD;
                                        let profit_or_stop_loss_percentage = transaction.profit_or_stop_loss_percentage || transaction.currentPriceChangePercentage;

                                        return (
                                            <Tr key={id} bg={is_finished ? "" : "gray.700"} color="gray.300" fontWeight="600">
                                                <Td>{limit*(currentPage - 1) + idx + 1}</Td>
                                                <Td><Text color="orange">{ticker}</Text></Td>
                                                <Td>{isLongOrder ? <Text color="green.500">LONG</Text> : <Text color="red.500">SHORT</Text>}</Td>
                                                <Td><Text color="orange.700">{price_when_buy ? `${price_when_buy}$` : ``}</Text></Td>
                                                <Td><Text color="blue.300">{price_when_sold ? `${price_when_sold}$` : ``}</Text></Td>
                                                <Td>{ profit_or_stop_loss_percentage && <Text color={profit_or_stop_loss_percentage > 0 ? "lime" : "red.500"}>{profit_or_stop_loss_percentage}%</Text> }</Td>
                                                <Td>{ buy_at && <><FormattedDate value={buy_at} /> <FormattedTime value={buy_at} /></> }</Td>
                                                <Td>{ sold_at && <><FormattedDate value={sold_at} /> <FormattedTime value={sold_at} /></> }</Td>
                                                <Td>{is_finished ? <Badge variant='solid' colorScheme='gray'>DONE</Badge> : <Badge variant='solid' colorScheme='green'>ACTIVE</Badge>}</Td>
                                                <Td>{ created_at && <><FormattedDate value={created_at} /> <FormattedTime value={created_at} /></> }</Td>
                                                <Td>
                                                    <Button colorScheme="red"
                                                        justifyContent="flex-start" gap={2} size="sm"
                                                        onClick={() => handleCancelOrder(id)}
                                                    >
                                                        <MdCancel />
                                                    </Button>
                                                </Td>
                                            </Tr>
                                        )
                                    })
                                }
                            </Tbody>
                        </Table>
                        {
                            allTransactions.length === 0 &&
                            <Text fontSize="12px" fontWeight="normal" color="gray.300" mx={4} my={4}>No wallet trackings available.</Text>
                        }
                    </TableContainer>
                    <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} width="100%" justify="flex-start" gap={2} paddingLeft={2} paddingBottom={3}>
                        <HStack color="gray.300">
                            <Text fontSize="11px">Showing 0 to 10 of 0 rows</Text>
                            <Select w={20} color="white" size="sm" borderColor="gray.500" value={limit}
                                onChange={
                                    (evt) => {
                                        setLimit(parseInt(evt.target.value));
                                    }
                                }
                            >
                                {
                                    PAGINATION_LIST.map((pagination, idx) => (
                                        <option key={`main-wallet-${idx}`} value={pagination} style={{ color: "black" }}>{pagination}</option>
                                    ))
                                }
                            </Select>
                            <Text fontSize="11px">rows per page</Text>
                        </HStack>
                        <HStack>
                            <Button variant="ghost" color="gray.300" borderWidth="1px" borderColor="gray.300" _hover={{ opacity: 0.7 }} _active="transparent" 
                                justifyContent="flex-start" gap={2} size="sm" isDisabled={currentPage === 1} 
                                onClick={() => handlePreviousPage()}
                            >
                                <MdSkipPrevious /> <Text fontSize="12px" fontWeight="normal">Previous</Text>
                            </Button>
        
                            <Button variant="ghost" color="gray.300" borderWidth="1px" borderColor="gray.300" _hover={{ opacity: 0.7 }} _active="transparent" 
                                justifyContent="flex-start" gap={2} size="sm" isDisabled={totalCount <= currentPage*limit} 
                                onClick={() => handleNextPage()}
                            >
                                <Text fontSize="12px" fontWeight="normal">Next</Text> <MdSkipNext /> 
                            </Button>
                        </HStack>
                    </Flex>
                </Flex>
            </Flex>

            {
                isLoading &&
                <Center w="100%">
                    <CircularProgress position="fixed" top="20vh" isIndeterminate size="200px" zIndex={9999}>
                        <CircularProgressLabel fontSize="24px" fontWeight="700" color="#2196f3">Loading</CircularProgressLabel>
                    </CircularProgress>
                </Center>
            }
        </Flex>
    )
}

export default ListAutoTradeTransaction;